import React, { useState, useEffect } from 'react';
import { X, Check, Scale } from 'lucide-react';
import PropTypes from 'prop-types';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const CaseTypeDropdown = ({
  isOpen,
  onClose,
  selectedCaseType = '',
  onCaseTypeChange,
  buttonRef
}) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 256, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  const caseTypeOptions = [
    {
      value: '',
      label: 'All Cases',
      description: 'Show both felony and misdemeanor cases'
    },
    {
      value: 'Felony',
      label: 'Felony Cases',
      description: 'Serious crimes with case numbers ending in F'
    },
    {
      value: 'Misdemeanor',
      label: 'Misdemeanor Cases',
      description: 'Less serious crimes with case numbers ending in M'
    }
  ];

  // Handle case type selection
  const handleCaseTypeSelect = (caseType) => {
    onCaseTypeChange(caseType);
    onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < caseTypeOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < caseTypeOptions.length) {
          handleCaseTypeSelect(caseTypeOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  // Handle clear
  const handleClear = () => {
    onCaseTypeChange('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-64 sm:w-64'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '280px' // Ensure minimum usable width
              }
        }
        onKeyDown={handleKeyDown}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Case Type</h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Selected Case Type */}
          {selectedCaseType && (
            <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <div className="text-xs font-medium text-blue-700 mb-1">
                Selected:
              </div>
              <div className="text-sm text-blue-900">
                {caseTypeOptions.find((opt) => opt.value === selectedCaseType)
                  ?.label || selectedCaseType}
              </div>
            </div>
          )}

          {/* Case Type Options */}
          <div className="space-y-2">
            {caseTypeOptions.map((option, index) => {
              const isSelected = option.value === selectedCaseType;
              const isFocused = focusedIndex === index;
              return (
                <button
                  key={option.value}
                  onClick={() => handleCaseTypeSelect(option.value)}
                  className={`w-full p-4 text-left border rounded-lg transition-colors ${
                    isSelected
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : isFocused
                        ? 'bg-gray-50 border-gray-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Scale className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="font-medium text-sm">
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {option.description}
                        </div>
                      </div>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-4">
            <button
              onClick={handleClear}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={!selectedCaseType}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

CaseTypeDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCaseType: PropTypes.string,
  onCaseTypeChange: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default CaseTypeDropdown;
